import { useQuery } from '@tanstack/react-query';
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Sparkles, Lightbulb } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { EmptyState } from '../components/ui/EmptyState';
import { GettingStartedCard } from '../components/GettingStartedCard';
import { ProfileCompleteness } from '../components/profile/ProfileCompleteness';
import { NextActions } from '../components/profile/NextActions';
import { BeneficiaryReview } from '../components/profile/BeneficiaryReview';

export function DashboardPage() {
  const { data: snapshot, isLoading: snapshotLoading } = useQuery({
    queryKey: ['snapshot'],
    queryFn: () => api.getUnifiedSnapshot(),
  });

  const { data: documentsData } = useQuery({
    queryKey: ['documents'],
    queryFn: () => api.getDocuments(),
  });
  const hasDocuments = (documentsData?.documents?.length ?? 0) > 0;

  const { data: alertCount } = useQuery({
    queryKey: ['alertCount'],
    queryFn: () => api.getUnreadAlertCount(),
  });

  const { data: agentRunsData } = useQuery({
    queryKey: ['agentRuns'],
    queryFn: () => api.getAgentRuns(),
  });

  const { data: recommendationsData } = useQuery({
    queryKey: ['recommendations'],
    queryFn: () => api.getRecommendations(),
  });

  const { data: chatSessionsData } = useQuery({
    queryKey: ['chatSessions'],
    queryFn: () => api.getChatSessions(),
  });

  if (snapshotLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const recentRuns = agentRunsData?.data?.slice(0, 3) ?? [];
  const topRecommendations = recommendationsData?.data?.slice(0, 3) ?? [];
  const chatSessionCount = chatSessionsData?.data?.length ?? 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Your wealth strategy at a glance</p>
      </div>

      {/* Profile or first-time guidance */}
      {hasDocuments ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ProfileCompleteness />
            <NextActions />
          </div>
          <BeneficiaryReview />
        </div>
      ) : (
        <GettingStartedCard />
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Analyses"
          value={agentRunsData?.pagination?.total ?? 0}
          change=""
          trend="neutral"
          description="Total analyses run"
        />
        <StatCard
          title="Recommendations"
          value={recommendationsData?.pagination?.total ?? 0}
          change=""
          trend="neutral"
          description="AI-generated insights"
        />
        <StatCard
          title="Chat Sessions"
          value={chatSessionCount}
          change=""
          trend="neutral"
          description="Active conversations"
        />
        <StatCard
          title="Unread Alerts"
          value={alertCount?.count ?? 0}
          change=""
          trend={alertCount?.count > 0 ? 'warning' : 'success'}
          description={alertCount?.count > 0 ? 'Action needed' : 'All clear'}
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Agent Runs */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Analyses</h2>
            <Link to="/agents" className="text-sm text-legacy-600 hover:text-legacy-700">
              View all
            </Link>
          </div>
          {recentRuns.length > 0 ? (
            <div className="space-y-3">
              {recentRuns.map((run: any) => (
                <div key={run.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="p-1.5 bg-legacy-100 rounded">
                      <Sparkles className="h-4 w-4 text-legacy-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 capitalize">{run.agentType} Analysis</p>
                      <p className="text-xs text-gray-500">
                        {new Date(run.startedAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <StatusBadge status={run.status} />
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={Sparkles}
              title="No analyses yet"
              description={
                <>
                  <Link to="/agents" className="text-legacy-600 hover:underline">
                    Run an analysis
                  </Link>{' '}
                  to generate recommendations.
                </>
              }
            />
          )}
        </div>

        {/* Top Recommendations */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Top Recommendations</h2>
            <Link to="/recommendations" className="text-sm text-legacy-600 hover:text-legacy-700">
              View all
            </Link>
          </div>
          {topRecommendations.length > 0 ? (
            <div className="space-y-3">
              {topRecommendations.map((rec: any) => (
                <div key={rec.id} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{rec.title}</p>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{rec.summary}</p>
                    </div>
                    <PriorityBadge priority={rec.priority} />
                  </div>
                  <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                    <span className="capitalize">{rec.agentType} Analysis</span>
                    <span>{Math.round(rec.confidence * 100)}% confidence</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={Lightbulb}
              title="No recommendations yet"
              description="Run an analysis to generate personalized recommendations."
            />
          )}
        </div>
      </div>

      {/* Data Sources Status */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Data Sources</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SourceCard
            name="LevelUp Home"
            status={snapshot?.sources?.home?.status ?? 'pending'}
            lastSync={snapshot?.sources?.home?.lastSync}
          />
          <SourceCard
            name="Reconcile"
            status={snapshot?.sources?.reconcile?.status ?? 'pending'}
            lastSync={snapshot?.sources?.reconcile?.lastSync}
          />
          <SourceCard
            name="CRM"
            status={snapshot?.sources?.crm?.status ?? 'pending'}
            lastSync={snapshot?.sources?.crm?.lastSync}
          />
        </div>
      </div>
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: string | number;
  change: string;
  trend: 'up' | 'down' | 'neutral' | 'warning' | 'success';
  description: string;
}

function StatCard({ title, value, change, trend, description }: StatCardProps) {
  const trendIcons = {
    up: <TrendingUp className="h-4 w-4 text-green-500" />,
    down: <TrendingDown className="h-4 w-4 text-red-500" />,
    neutral: null,
    warning: <AlertTriangle className="h-4 w-4 text-yellow-500" />,
    success: <CheckCircle className="h-4 w-4 text-green-500" />,
  };

  return (
    <div className="card">
      <p className="text-sm text-gray-600">{title}</p>
      <div className="flex items-baseline space-x-2 mt-1">
        <p className="text-2xl font-semibold text-gray-900">{value}</p>
        {change && (
          <span className="flex items-center text-sm text-gray-500">
            {trendIcons[trend]}
            <span className="ml-1">{change}</span>
          </span>
        )}
      </div>
      <p className="text-xs text-gray-500 mt-1">{description}</p>
    </div>
  );
}

interface SourceCardProps {
  name: string;
  status: string;
  lastSync: string | null;
}

function SourceCard({ name, status, lastSync }: SourceCardProps) {
  const statusColors = {
    current: 'bg-green-100 text-green-800',
    stale: 'bg-yellow-100 text-yellow-800',
    error: 'bg-red-100 text-red-800',
    pending: 'bg-gray-100 text-gray-800',
  };

  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
      <div>
        <p className="font-medium text-gray-900">{name}</p>
        <p className="text-xs text-gray-500">
          {lastSync ? `Last sync: ${new Date(lastSync).toLocaleString()}` : 'Not synced'}
        </p>
      </div>
      <span
        className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[status as keyof typeof statusColors] ?? statusColors.pending}`}
      >
        {status}
      </span>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    completed: 'bg-green-100 text-green-800',
    running: 'bg-blue-100 text-blue-800',
    failed: 'bg-red-100 text-red-800',
    pending: 'bg-gray-100 text-gray-800',
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status] ?? colors.pending}`}>
      {status}
    </span>
  );
}

function PriorityBadge({ priority }: { priority: string }) {
  const colors: Record<string, string> = {
    critical: 'bg-red-100 text-red-800',
    high: 'bg-orange-100 text-orange-800',
    medium: 'bg-yellow-100 text-yellow-800',
    low: 'bg-green-100 text-green-800',
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[priority] ?? colors.medium}`}>
      {priority}
    </span>
  );
}
